/**
 * External dependencies
 */
import React, { useState } from "react";
import StoryCard from "../../story-card/component";

/**
 * Internal dependencies
 */
import {
	useUrlParamState,
	useFilters,
	usePosts,
	useTermsAsSelectOptions,
} from "./hooks";
import { FiltersLoader, ResultsLoader, Spinner } from "./loaders";
import {
	ErrorMessage,
	MultiSelect,
	NeutralMessage,
} from "./components";
import { Post } from "./api/types";

/**
 * Constants
 */

/**
 * Application
 */
interface AppProps {
	perPage: number;
	texts: Record<string, string>;
}

const App = ({ perPage, texts }: AppProps) => {
	/**
	 * Load filter data
	 */
	const { data, error } = useFilters();
	const { themes, categories } = data || {};
	const themeOptions = useTermsAsSelectOptions(themes);
	const categoryOptions = useTermsAsSelectOptions(categories);

	/**
	 * Setup filter state
	 */
	const [selectedThemes, setSelectedThemes] = useUrlParamState(
		"ytk_themes",
		[] as (number | string)[]
	);
	const [selectedCategories, setSelectedCategories] = useUrlParamState(
		"categories",
		[] as (number | string)[]
	);

	/**
	 * Build query params for Posts API
	 */
	const queryParams = {
		themes: selectedThemes || [],
		categories: selectedCategories || [],
	};

	/**
	 * Error and loading state
	 */
	if (error) {
		return (
			<ErrorMessage
				title={texts?.filterErrorTitle}
				text={texts?.filterErrorText}
			/>
		);
	}

	if (!data) {
		return (
			<div className="em-block-story-feed__app">
				<FiltersLoader numOfItems={2} />
				<ResultsLoader numOfItems={perPage} />
			</div>
		);
	}

	// Get the translation strings from PolyLang
	// @ts-ignore
	const translate = window.emYtkBlocksConfig;

	return (
		<div className="em-block-story-feed__app">
			<form className="em-block-story-feed__filters" aria-label={translate?.text?.StoryFilteringMenu ?? "Story filtering menu"}>
				<MultiSelect
					inputId="story-filter-topic-input"
					label={texts?.themesLabel || ""}
					placeholder={texts?.themesPlaceholder || ""}
					options={themeOptions}
					selected={selectedThemes}
					setSelected={setSelectedThemes}
				/>
				<MultiSelect
					inputId="story-filter-category-input"
					label={texts?.categoriesLabel || ""}
					placeholder={texts?.categoriesPlaceholder || ""}
					options={categoryOptions}
					selected={selectedCategories}
					setSelected={setSelectedCategories}
				/>
			</form>
			<Results
				perPage={perPage}
				texts={texts}
				queryParams={queryParams}
				errorTitle={texts?.postsErrorTitle}
				errorText={texts?.postsErrorText}
				noPostsFoundTitle={texts?.noPostsFoundTitle}
				noPostsFoundText={texts?.noPostsFoundText}
				loadMoreButtonText={texts?.loadMoreButtonText}
				renderPost={(post) => {

					const storyThemes = themes?.filter((theme) =>
						post.themes?.includes(theme.id)
					);

					const storyCategories = categories?.filter((category) =>
						post.categories?.includes(category.id)
					);

					return <StoryCard key={post.id} {...post} themes={storyThemes} categories={storyCategories} />;
				}}
			/>
		</div>
	);
};

/**
 * Results
 */
interface ResultsProps {
	perPage: number;
	texts: Record<string, string>;
	queryParams: object;
	errorTitle?: string;
	errorText?: string;
	noPostsFoundTitle?: string;
	noPostsFoundText?: string;
	loadMoreButtonText?: string;
	renderPost: (post: Post) => React.ReactNode;
}

const Results = ({
	perPage,
	queryParams,
	errorTitle = "Error loading posts.",
	errorText,
	noPostsFoundTitle = 'No posts found.',
	noPostsFoundText,
	loadMoreButtonText = "Load more",
	renderPost,
}: ResultsProps) => {
	const { data, error, isLoadingMore, canLoadMore, loadMore, isEmpty } = usePosts({
		perPage,
		queryParams,
	});

	if (error) {
		return <ErrorMessage title={errorTitle} text={errorText} />;
	}

	if (!data) {
		return (
			<ResultsLoader numOfItems={perPage} />
		);
	}

	if (isEmpty) {
		return (
			<NeutralMessage title={noPostsFoundTitle} text={noPostsFoundText} />
		);
	}
	return (
		<div className="em-block-story-feed__results">
			<div className="em-block-story-feed__posts">
				{data.map(renderPost)}
			</div>
			<div className="em-block-story-feed__more">
				{canLoadMore && (
					<button className="em-button" onClick={loadMore}>
						{loadMoreButtonText}
					</button>
				)}

				{isLoadingMore && <Spinner />}
			</div>
		</div>
	);
};

export default App;
